import React, { useState, useEffect } from "react";
import { ASSETS, BUSINESS } from "../lib/assets";
import SectionHeading from "../components/SectionHeading";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PRODUCT_CATEGORIES } from "../components/AdminStoreTabs";
import { CATALOG_BY_SLUG } from "../lib/storeCatalog";
import { useCart } from "../lib/CartContext";

// Resolves backend-relative URLs (from Emergent Object Storage /api/files/...) into full URLs.
const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${backendUrl}${u}` : u);

const rubs = [
  {
    slug: "sweet-perfection",
    name: "Sweet Perfection",
    tagline: "The one that started it all",
    desc: "Our signature all-purpose seasoning. Sweet, smoky, and just enough heat. Perfect on brisket, ribs, chicken and wings.",
    size: "12.1 oz (343 g)",
    price: 12.99,
    tag: "Best Seller",
    img: "productSweetPerfection",
  },
  {
    slug: "silly-goose-rub",
    name: "Silly Goose's Rub",
    tagline: "Rib-rub royalty",
    desc: "Balanced pepper, brown sugar and paprika — built for ribs but works on damn near anything with a bone in it.",
    size: "12.2 oz (345 g)",
    price: 12.99,
    tag: "Fan Favorite",
    img: "productSillyGoose",
  },
  {
    slug: "honey-jalapeno-bliss",
    name: "Honey Jalapeño Bliss",
    tagline: "Sweet meets heat",
    desc: "Real honey powder with jalapeño kick. Killer on chicken wings, corn on the cob and grilled shrimp.",
    size: "12.3 oz (348 g)",
    price: 12.99,
    tag: "New",
    img: "productHoneyJalapeno",
  },
  {
    slug: "big-moes-kickin-rub",
    name: "Big Moe's Kickin' Rub",
    tagline: "The new kick in the pit",
    desc: "Bold, punchy all-purpose rub with a low-slow kick. Built for wings, ribs, brisket burnt ends, and anything that needs a wake-up call.",
    size: "12 oz (340 g)",
    price: 12.99,
    tag: "New",
    img: "productTillmanBeefRub",
  },
];

const sauces = [
  { slug: "chipotle-apple", name: "Tillman's Chipotle Apple", desc: "Smoky chipotle warmth balanced with sweet apple. Killer on pork butt, ribs, and grilled chicken thighs.", size: "21 oz (595 g)", price: 12.99, img: "productChipotleAppleSauce", tag: "New" },
  { slug: "florida-gold", name: "Tillman's Florida Gold", desc: "Mustard-forward gold sauce. Perfect on pulled pork & smoked chicken. Competition-grade.", size: "20 oz (567 g)", price: 12.99, img: "productFloridaGoldSauce" },
  { slug: "grandmas-red-sauce", name: "Tillman's Grandma's Red Sauce", desc: "Rich tomato-molasses classic. Small-batch, slow-reduced from a family recipe.", size: "21 oz (595 g)", price: 12.99, img: "productGrandmasRedSauce" },
  { slug: "last-call-sauce", name: "Last Call BBQ Sauce", desc: "Sweet & spicy — the sauce that closes out the night. Cold Beer BBQ Competition Team recipe.", size: "21.5 oz (576 g)", price: 12.99, img: "productLastCallSauce", tag: "Competition" },
];

const bundles = [
  {
    slug: "family-3-pack",
    name: "The Family 3-Pack (16 oz)",
    tagline: "One of each — supersized",
    desc: "Silly Goose's Rib Rub + Sweet Perfection + Honey Jalapeño Bliss. Party-size shakers. Free shipping.",
    size: "3 × 16 oz",
    price: 33.00,
    tag: "Bundle Deal",
    img: "productTrio16oz",
  },
  {
    slug: "holiday-gift-pack",
    name: "Holiday Gift Pack",
    tagline: "The perfect gift for the pit master in your life",
    desc: "All four signature rubs — Honey Jalapeño Bliss, Sweet Perfection, Big Moe's Kickin' Rub, and Silly Goose's Rib Rub. Ready to gift, tied with a bow of smoke.",
    size: "4 rubs · gift-ready",
    price: 45.00,
    tag: "🎁 Holiday Special",
    img: "productHolidayGiftPack",
  },
];

const merch = [
  {
    slug: "florida-brisket-tee",
    name: "Florida Brisket Tee",
    desc: "Black cotton tee — Sunshine State plate print on the back, small When Pigs Fly logo on the front chest, Old Glory on the sleeve.",
    price: 25.00,
    img: "floridaBrisketTee",
    // Live inventory per size — when a size hits 0 it's greyed out and unbuyable.
    // When ALL sizes are 0 the whole tee flips to Sold Out.
    sizes: { S: 0, M: 0, L: 4, XL: 15, "2XL": 0, "3XL": 0 },
  },
  {
    slug: "rubbin-racin-tee",
    name: "Rubbin' & Racin' Racing Team Tee",
    desc: "Black cotton tee — When Pigs Fly Racing Team back print featuring the pig, chicken and cow on a checkered flag. Good rub · hot pit · fast times.",
    price: 25.00,
    img: "teeRubbinRacin",
    imgFront: "teeRubbinRacinFront",
    sizes: { S: 5, M: 10, L: 10, XL: 10, "2XL": 5, "3XL": 5 },
  },
  {
    slug: "neon-bbq-tee",
    name: "When Pigs Fly BBQ Neon Tee",
    desc: "Black cotton tee — vintage neon sign back print. When Pigs Fly BBQ in glowing red script with the flying pig cutting through the letters.",
    price: 25.00,
    img: "teeNeonBbq",
    imgFront: "teeNeonBbqFront",
    sizes: { S: 5, M: 10, L: 10, XL: 10, "2XL": 5, "3XL": 5 },
  },
  {
    slug: "bbq-lifestyle-tee",
    name: "BBQ — It's a Lifestyle Tee",
    desc: "Black cotton tee — neon pig butcher chart back print. Faith · Family · Friends · BBQ. Front chest hit with the When Pigs Fly mark.",
    price: 25.00,
    img: "teeBbqLifestyle",
    imgFront: "teeBbqLifestyleFront",
    sizes: { S: 5, M: 10, L: 10, XL: 10, "2XL": 5, "3XL": 5 },
  },
  {
    slug: "team-felicia-tee",
    name: "We Support Team Felicia Tee",
    desc: "Natural cream cotton tee — CMT awareness ribbon with floral back print. A portion of every sale goes toward the fight against CMT.",
    price: 25.00,
    img: "teeTeamFelicia",
    imgFront: "teeTeamFeliciaFront",
    sizes: { S: 5, M: 10, L: 10, XL: 10, "2XL": 5, "3XL": 5 },
  },
  {
    slug: "silly-goose-hat",
    name: "Silly Goose Club Trucker Hat",
    desc: "Black flat-brim trucker with cream Silly Goose Club embroidery and white rope brim. Snapback closure, one-size-fits-most.",
    size: "One size",
    price: 35.00,
    img: "productSillyGooseHat",
  },
  {
    slug: "wpf-cap",
    name: "When Pigs Fly Cap",
    desc: "Structured black perforated cap with the When Pigs Fly Apparel Co. leather patch. Legacy Cut Above visor made from recycled content.",
    size: "One size",
    price: 35.00,
    img: "productCap",
  },
];

// Legacy fallback kept for hardcoded fallback links.
const paypalPayLink = (_amount) => "https://www.paypal.biz/2guysgrilling";

const fmtUSD = (n) => `$${n.toFixed(2)}`;

// Single Add-to-Cart button — replaces the old per-product PayPal button pattern.
// All checkouts now flow through one PayPal button in the CartDrawer, which fixes
// the "zoid destroyed all components" issue caused by rendering 30+ buttons on one page.
function AddToCartRow({ price, testIdPrefix, size, productName, image, slug, disabled = false, note = "", variant = "" }) {
  const [qty, setQty] = useState(1);
  const cart = useCart();

  const add = () => {
    if (disabled) return;
    cart.addItem({
      slug: slug || productName,
      name: productName,
      price,
      image,
      variant,  // e.g. selected tee size
    }, qty);
    setQty(1);
  };

  return (
    <div className="mt-6 border-t border-ink pt-4" data-testid={`${testIdPrefix}-buy-row`}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="font-mono text-sm uppercase tracking-[0.12em] text-ink" data-testid={`${testIdPrefix}-total`}>
            {fmtUSD(price * qty)}
          </div>
          {size && (
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{size}</div>
          )}
        </div>
        <QtyStepper qty={qty} setQty={setQty} testIdPrefix={testIdPrefix} />
      </div>

      {disabled ? (
        <div className="mt-4 border-2 border-ink/20 bg-ink/5 px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.24em] text-ink/60">
          Pick a size to add to cart
        </div>
      ) : (
        <button
          type="button"
          onClick={add}
          data-testid={`${testIdPrefix}-add-to-cart`}
          className="mt-4 flex w-full items-center justify-center gap-2 border-2 border-ink bg-ink px-4 py-3 font-alfa text-sm uppercase tracking-[0.24em] text-bone transition-colors hover:bg-transparent hover:text-ink"
        >
          <ShoppingBag size={16} /> Add to Cart
        </button>
      )}
      {note && (
        <p className="mt-2 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-ink/50">{note}</p>
      )}
    </div>
  );
}
// Alias so we don't have to rename every call site.
const BuyNowRow = AddToCartRow;

function QtyStepper({ qty, setQty, testIdPrefix }) {
  return (
    <div className="inline-flex items-stretch border border-current font-mono text-xs uppercase tracking-[0.16em]">
      <button
        type="button"
        aria-label="Decrease quantity"
        data-testid={`${testIdPrefix}-qty-dec`}
        onClick={() => setQty((q) => Math.max(1, q - 1))}
        className="flex h-9 w-9 items-center justify-center border-r border-current transition-colors hover:bg-current hover:text-white"
      >
        <Minus size={14} />
      </button>
      <span
        data-testid={`${testIdPrefix}-qty`}
        className="flex h-9 w-10 items-center justify-center tabular-nums"
      >
        {qty}
      </span>
      <button
        type="button"
        aria-label="Increase quantity"
        data-testid={`${testIdPrefix}-qty-inc`}
        onClick={() => setQty((q) => Math.min(99, q + 1))}
        className="flex h-9 w-9 items-center justify-center border-l border-current transition-colors hover:bg-current hover:text-white"
      >
        <Plus size={14} />
      </button>
    </div>
  );
}

function SoldOutRow({ price, size }) {
  return (
    <div className="mt-6 border-t border-ink pt-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="font-mono text-sm uppercase tracking-[0.12em] text-ink line-through opacity-50">
            {fmtUSD(price)}
          </div>
          {size && (
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{size}</div>
          )}
        </div>
        <span className="border border-ink px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-ink">
          Restocking soon
        </span>
      </div>
      <button
        type="button"
        disabled
        aria-disabled="true"
        data-testid="sold-out-btn"
        className="mt-4 inline-flex w-full cursor-not-allowed items-center justify-center gap-2 border-2 border-ink/40 bg-ink/40 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.24em] text-bone"
      >
        Sold Out
      </button>
    </div>
  );
}

// Merch image with hover-flip (back → front) and back/front thumbnail toggle.
// Works on both desktop (hover) and mobile (tap thumbnail).
function MerchImage({ m, allSoldOut, testIdPrefix, imgBack, imgFront }) {
  const hasFront = Boolean(m.imgFront);
  // `view` = which side is showing. null = default (back), controlled by hover
  // If user taps a thumbnail on mobile, it becomes 'back' or 'front' explicitly.
  const [view, setView] = React.useState(null);
  const [hovering, setHovering] = React.useState(false);

  const showingFront = hasFront && (view === "front" || (view === null && hovering));
  const back = imgBack || ASSETS[m.img];
  const front = imgFront || (hasFront ? ASSETS[m.imgFront] : null);

  return (
    <div
      className="relative border-b border-ink bg-white"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Main image — back by default, flips to front on hover or thumbnail select */}
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <img
          src={back}
          alt={`${m.name} — back`}
          data-testid={`${testIdPrefix}-img-back`}
          className={`absolute inset-0 h-full w-full object-contain p-4 transition-opacity duration-300 ${showingFront ? "opacity-0 pointer-events-none" : "opacity-100"} ${allSoldOut ? "opacity-60 grayscale" : ""}`}
        />
        {hasFront && (
          <img
            src={front}
            alt={`${m.name} — front`}
            data-testid={`${testIdPrefix}-img-front`}
            className={`absolute inset-0 h-full w-full object-contain p-4 transition-opacity duration-300 ${showingFront ? "opacity-100" : "opacity-0 pointer-events-none"} ${allSoldOut ? "opacity-60 grayscale" : ""}`}
          />
        )}
        {allSoldOut && (
          <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 border-y-2 border-bone/80 bg-ink/60 py-2 text-center font-alfa text-lg uppercase tracking-[0.3em] text-bone">
            Sold Out
          </div>
        )}
        {hasFront && (
          <span className="pointer-events-none absolute right-3 top-3 border border-ink bg-bone px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink">
            {showingFront ? "Front" : "Back"}
          </span>
        )}
      </div>

      {/* Back / Front thumbnails below main image */}
      {hasFront && (
        <div className="flex items-center gap-2 border-t border-ink bg-bone/60 px-4 py-3">
          {[
            { key: "back", label: "Back", src: back },
            { key: "front", label: "Front", src: front },
          ].map((thumb) => {
            const isActive = (view === thumb.key) || (view === null && ((thumb.key === "front") === showingFront));
            return (
              <button
                key={thumb.key}
                type="button"
                onClick={() => setView(thumb.key)}
                onMouseEnter={() => setView(thumb.key)}
                data-testid={`${testIdPrefix}-thumb-${thumb.key}`}
                className={`group/th flex items-center gap-2 border px-2 py-1 transition-colors ${isActive ? "border-ink bg-white" : "border-ink/40 bg-bone hover:border-ink"}`}
              >
                <span className="block h-10 w-12 overflow-hidden border border-ink/20 bg-white">
                  <img src={thumb.src} alt="" className="h-full w-full object-contain" />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                  {thumb.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}



function MerchCard({ m, idx, isLast, total, imgBack, imgFront }) {
  const [sizes, setSizes] = useState(m.sizes || null);
  const [selected, setSelected] = useState(null);
  const testIdPrefix = `merch-${idx}`;

  // If this product has sizes, keep them in sync with the backend inventory (so
  // when you tweak stock in the owner dashboard the site auto-updates).
  React.useEffect(() => {
    if (!m.sizes) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/inventory/${encodeURIComponent(m.name)}`);
        if (!cancelled && data && data.sizes) setSizes(data.sizes);
      } catch { /* backend not seeded yet — fall back to hardcoded */ }
    })();
    return () => { cancelled = true; };
  }, [m.name, m.sizes]);

  // No auto-select — customer must pick a size explicitly (gating requirement).

  const totalStock = sizes ? Object.values(sizes).reduce((a, b) => a + b, 0) : null;
  const allSoldOut = sizes && totalStock === 0;

  return (
    <div
      data-testid={testIdPrefix}
      className={`group flex flex-col border-ink ${((idx + 1) % 2 !== 0) ? "sm:border-r" : ""} ${((idx + 1) % 3 !== 0) ? "lg:border-r" : ""} ${idx < total - (total % 3 || 3) ? "border-b" : ""}`}
    >
      {m.img && (
        <MerchImage
          m={m}
          allSoldOut={allSoldOut}
          testIdPrefix={testIdPrefix}
          imgBack={imgBack}
          imgFront={imgFront}
        />
      )}
      <div className="flex flex-1 flex-col p-8">
        <div className="flex items-center justify-between border-b border-current pb-3 font-mono text-[10px] uppercase tracking-[0.24em]">
          <span>Merch · 0{idx + 1}</span>
          <span>{sizes ? `${totalStock} in stock` : m.size}</span>
        </div>
        <h3 className="mt-6 font-alfa text-2xl uppercase leading-tight">{m.name}</h3>
        <p className="mt-3 font-serif text-sm leading-relaxed opacity-80">{m.desc}</p>

        {sizes && (
          <div className="mt-5">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.24em] text-ink/70">Pick your size</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(sizes).map(([label, n]) => {
                const out = n <= 0;
                const isActive = selected === label;
                return (
                  <button
                    key={label}
                    type="button"
                    disabled={out}
                    onClick={() => setSelected(label)}
                    data-testid={`${testIdPrefix}-size-${label}`}
                    aria-disabled={out}
                    className={`relative min-w-[3rem] border px-3 py-2 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                      out
                        ? "cursor-not-allowed border-ink/20 text-ink/30 line-through"
                        : isActive
                          ? "border-ink bg-ink text-bone"
                          : "border-ink text-ink hover:bg-ink hover:text-bone"
                    }`}
                  >
                    {label}
                    {!out && <span className="ml-1 text-[9px] opacity-60">({n})</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {allSoldOut ? (
          <SoldOutRow price={m.price} size="Restocking" />
        ) : (
          <BuyNowRow
            price={m.price}
            size={sizes ? (selected ? `Size ${selected}` : "Pick a size") : m.size}
            productName={m.name}
            variant={sizes ? selected : (m.size || "")}
            slug={m.slug || m.name}
            image={imgBack}
            disabled={!!(sizes && !selected)}
            testIdPrefix={testIdPrefix}
          />
        )}
      </div>
    </div>
  );
}

export default function Seasonings() {
  usePageMeta({
    title: "Seasonings & Merch",
    description: "Shop When Pigs Fly signature BBQ rubs, sauces and merch — Sweet Perfection, Silly Goose, Honey Jalapeño Bliss, Tillman's Beef Rub and more. Pre-filled PayPal checkout.",
    path: "/seasonings",
  });
  const [adminProducts, setAdminProducts] = useState([]);
  const [promotedItems, setPromotedItems] = useState([]);
  const [itemOverrides, setItemOverrides] = useState({}); // { slug: { name?, desc?, price?, size?, tag?, image_url?, hidden? } }
  const [adminCat, setAdminCat] = useState("All");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pRes, prRes, ovRes] = await Promise.all([
          api.get("/products"),
          api.get("/promoted-items"),
          api.get("/store-image-overrides"),
        ]);
        if (cancelled) return;
        if (Array.isArray(pRes.data)) setAdminProducts(pRes.data);
        if (Array.isArray(prRes.data)) setPromotedItems(prRes.data);
        if (ovRes.data && typeof ovRes.data === "object") setItemOverrides(ovRes.data);
      } catch { /* ignore — page still shows hardcoded items */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // Owner override wins field-by-field over the hardcoded catalog item.
  // Returns null if the owner hid this item so callers can filter it out.
  const applyOverride = (item) => {
    const ov = itemOverrides[item.slug];
    if (!ov) return item;
    if (ov.hidden) return null;
    return {
      ...item,
      name: ov.name || item.name,
      desc: ov.desc || item.desc,
      price: typeof ov.price === "number" ? ov.price : item.price,
      size: ov.size || item.size,
      tag: ov.tag || item.tag,
      _overrideImg: ov.image_url ? resolveUrl(ov.image_url) : null,
    };
  };
  const imgFor = (item) => item._overrideImg || ASSETS[item.img];
  // Apply overrides & drop hidden items for the public sections.
  const rubsShown = rubs.map(applyOverride).filter(Boolean);
  const saucesShown = sauces.map(applyOverride).filter(Boolean);
  const bundlesShown = bundles.map(applyOverride).filter(Boolean);
  const merchShown = merch.map(applyOverride).filter(Boolean);
  const holidayGiftPack = bundles.find((b) => b.slug === "holiday-gift-pack");
  const holidayGiftPackShown = holidayGiftPack ? applyOverride(holidayGiftPack) : null;
  const cart = useCart();
  const grabHolidayGiftPack = () => {
    if (!holidayGiftPackShown) return;
    cart.addItem({
      slug: holidayGiftPackShown.slug,
      name: holidayGiftPackShown.name,
      price: Number(holidayGiftPackShown.price),
      image: imgFor(holidayGiftPackShown),
      variant: holidayGiftPackShown.size || "",
    }, 1);
  };
  // Featured product = the one admin marked as featured (top of the drops).
  const featured = adminProducts.find((p) => p.featured);
  // Build a unified New Drops list: admin products (excluding featured, since it renders above) + promoted hardcoded items.
  const promotedAsProducts = promotedItems
    .map((pi) => {
      const it = CATALOG_BY_SLUG[pi.slug];
      if (!it) return null;
      const ov = itemOverrides[pi.slug] || {};
      if (ov.hidden) return null;  // hidden items don't show up in New Drops either
      return {
        id: `catalog-${pi.slug}`,
        name: ov.name || it.name,
        desc: ov.desc || it.desc,
        price: typeof ov.price === "number" ? ov.price : it.price,
        size: ov.size || it.size,
        tag: ov.tag || it.tag || "",
        category: it.category,
        image_url: ov.image_url ? resolveUrl(ov.image_url) : it.img,
        _isCatalog: true,
      };
    })
    .filter(Boolean);
  const newDropsAll = [
    ...adminProducts.filter((p) => !p.featured),
    ...promotedAsProducts,
  ];
  const filteredAdmin = adminCat === "All"
    ? newDropsAll
    : newDropsAll.filter((p) => p.category === adminCat);
  return (
    <div>
      {/* HERO */}
      {/* Free shipping banner — always visible above the store */}
      <div data-testid="free-shipping-banner" className="border-b-2 border-ink bg-red-700 py-2 text-center font-mono text-xs uppercase tracking-[0.24em] text-bone">
        🚚 FREE Shipping on every order over $25 · Flat $6.99 otherwise
      </div>

      <section className="border-b border-ink py-16" data-testid="seasonings-hero">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-4 md:grid-cols-12 md:px-8">
          <div className="md:col-span-7">
            <div className="eyebrow mb-3 text-red-700">🎁 Holiday Gift Pack · Secure Checkout via PayPal</div>
            <h1 className="font-rye text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-7xl">Seasonings<br />&amp; Merch</h1>
            <p className="mt-6 max-w-xl font-serif text-base leading-relaxed text-ink/80">
              The perfect gift for the pit master in your life — all four signature rubs
              (Honey Jalapeño Bliss, Sweet Perfection, Big Moe's Kickin' Rub, and Silly Goose's Rib Rub)
              boxed at the pit and ready to gift. Every jar is the same recipe we use for competition cooks.
              Also available at Ace Hardware, walmart.com, Harts Meat Market &amp; Central Florida Beef.
            </p>
            <button
              type="button"
              onClick={grabHolidayGiftPack}
              disabled={!holidayGiftPackShown}
              data-testid="hero-gift-cta"
              className="mt-8 inline-flex items-center gap-3 border-2 border-ink bg-ink px-8 py-4 font-alfa text-lg uppercase tracking-[0.2em] text-bone transition-colors hover:bg-transparent hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ShoppingBag size={18} />
              Add Holiday Gift Pack — ${holidayGiftPackShown ? holidayGiftPackShown.price.toFixed(0) : "45"}
            </button>
          </div>
          <div className="md:col-span-5">
            {holidayGiftPackShown && (
              <>
                <div className="grain-block relative aspect-[4/5] w-full overflow-hidden border border-ink bg-white shadow-hard">
                  <img src={imgFor(holidayGiftPackShown)} alt={`${holidayGiftPackShown.name} — all four When Pigs Fly signature rubs`} className="h-full w-full object-contain p-6" data-testid="holiday-gift-pack-image" />
                  <span className="absolute left-4 top-4 border-2 border-bone bg-red-700 px-3 py-1 font-alfa text-sm uppercase tracking-[0.2em] text-bone">
                    🎁 Holiday Special · ${holidayGiftPackShown.price.toFixed(0)}
                  </span>
                </div>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
                  Honey Jalapeño Bliss · Sweet Perfection · Big Moe's Kickin' Rub · Silly Goose's Rib Rub
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* FRESH DROPS — admin-added products + promoted storefront items, live PayPal checkout */}
      {(newDropsAll.length > 0 || featured) && (
        <section className="border-b border-ink bg-white py-20 md:py-24" data-testid="admin-products-section">
          <div className="mx-auto max-w-[1400px] px-4 md:px-8">
            <SectionHeading eyebrow="Fresh from the pit" title="New Drops" subtitle="Just added by the owner — one-of-a-kind items with live PayPal checkout." />

            {/* Featured Spotlight — top of the drops */}
            {featured && (
              <div data-testid="featured-spotlight" className="mt-10 grid grid-cols-1 gap-0 border-2 border-ink shadow-hard md:grid-cols-12">
                <div className="relative aspect-[4/3] overflow-hidden bg-white md:col-span-6">
                  <img src={resolveUrl(featured.image_url)} alt={featured.name} className="h-full w-full object-contain p-8" />
                  <span className="absolute left-4 top-4 border-2 border-bone bg-red-700 px-3 py-1 font-alfa text-sm uppercase tracking-[0.2em] text-bone">
                    ★ Spotlight
                  </span>
                  {featured.category && (
                    <span className="absolute right-4 top-4 border border-ink bg-bone px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink">
                      {featured.category}
                    </span>
                  )}
                </div>
                <div className="flex flex-col justify-center border-l border-ink p-8 md:col-span-6 md:p-14">
                  <div className="eyebrow mb-3 text-red-700">Featured this week</div>
                  <h3 className="font-rye text-3xl uppercase leading-none md:text-5xl">{featured.name}</h3>
                  {featured.desc && <p className="mt-4 max-w-md font-serif text-base leading-relaxed opacity-85">{featured.desc}</p>}
                  <div className="mt-6">
                    <BuyNowRow
                      price={Number(featured.price)}
                      size={featured.size || ""}
                      productName={featured.name}
                      slug={featured.id || featured.name}
                      image={resolveUrl(featured.image_url)}
                      testIdPrefix={`featured-${featured.id}`}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Category tabs */}
            <div className="mt-10 flex flex-wrap gap-2" data-testid="admin-products-cat-tabs">
              {["All", ...PRODUCT_CATEGORIES].map((c) => {
                const count = c === "All"
                  ? newDropsAll.length
                  : newDropsAll.filter((p) => p.category === c).length;
                if (c !== "All" && count === 0) return null;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setAdminCat(c)}
                    data-testid={`admin-products-cat-${c.toLowerCase()}`}
                    className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.24em] transition-colors ${adminCat === c ? "border-ink bg-ink text-bone" : "border-ink/40 text-ink hover:bg-ink/5"}`}
                  >
                    {c} ({count})
                  </button>
                );
              })}
            </div>

            {filteredAdmin.length === 0 ? (
              <p className="mt-12 border border-dashed border-ink p-10 text-center font-serif text-ink/60">
                Nothing in this category yet.
              </p>
            ) : (
              <div className="mt-8 grid grid-cols-1 gap-0 border border-ink sm:grid-cols-2 lg:grid-cols-3">
                {filteredAdmin.map((p, i) => (
                  <div
                    key={p.id}
                    data-testid={`admin-product-${p.id}`}
                    className={`group flex flex-col border-ink ${((i + 1) % 2 !== 0) ? "sm:border-r" : ""} ${((i + 1) % 3 !== 0) ? "lg:border-r" : ""} ${i < filteredAdmin.length - (filteredAdmin.length % 3 || 3) ? "border-b" : ""}`}
                  >
                    <div className="relative aspect-[4/5] overflow-hidden bg-white">
                      <img
                        src={resolveUrl(p.image_url)}
                        alt={p.name}
                        className="h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
                      />
                      {p.tag && (
                        <span className="absolute left-4 top-4 border border-bone bg-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-bone">
                          {p.tag}
                        </span>
                      )}
                      {p.category && (
                        <span className="absolute right-4 top-4 border border-ink bg-bone px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink">
                          {p.category}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="flex items-center justify-between border-b border-current pb-3 font-mono text-[10px] uppercase tracking-[0.24em]">
                        <span>New · 0{i + 1}</span>
                        <span>{p.size || "Limited"}</span>
                      </div>
                      <h3 className="mt-4 font-alfa text-xl uppercase leading-tight">{p.name}</h3>
                      {p.desc && <p className="mt-2 font-serif text-sm leading-relaxed opacity-80">{p.desc}</p>}
                      <BuyNowRow
                        price={Number(p.price)}
                        size={p.size || ""}
                        productName={p.name}
                        slug={p.id || p.name}
                        image={resolveUrl(p.image_url)}
                        testIdPrefix={`admin-product-${p.id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* THE RUBS */}
      <section className="border-b border-ink bg-white py-20 md:py-28" data-testid="rubs-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="The Rubs" title="Signature Seasonings" subtitle="Competition-grade blends we use every day at the pit. Each jar is built for a purpose." />

          <div className="mt-12 grid grid-cols-1 gap-0 border border-ink sm:grid-cols-2 lg:grid-cols-4">
            {rubsShown.map((p, i) => (
              <div
                key={p.name}
                data-testid={`rub-${i}`}
                className={`group flex flex-col border-ink ${((i + 1) % 2 !== 0) ? "sm:border-r" : ""} ${((i + 1) % 4 !== 0) ? "lg:border-r" : ""} ${i < rubsShown.length - (rubsShown.length % 4 || 4) ? "border-b" : ""}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-ink">
                  <img
                    src={imgFor(p)}
                    alt={p.name}
                    className={`h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105 ${p.soldOut ? "opacity-60 grayscale" : ""}`}
                  />
                  {p.soldOut ? (
                    <span
                      data-testid={`rub-${i}-sold-out-badge`}
                      className="absolute left-4 top-4 border border-bone bg-red-700 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-bone"
                    >
                      Sold Out
                    </span>
                  ) : (
                    <span className="absolute left-4 top-4 border border-bone bg-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-bone">
                      {p.tag}
                    </span>
                  )}
                  {p.soldOut && (
                    <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 border-y-2 border-bone/80 bg-ink/60 py-2 text-center font-alfa text-lg uppercase tracking-[0.3em] text-bone">
                      Sold Out
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="eyebrow mb-2 opacity-60">0{i + 1}</div>
                  <h3 className="font-alfa text-xl uppercase leading-tight">{p.name}</h3>
                  <p className="mt-1 font-serif text-xs italic opacity-70">{p.tagline}</p>
                  <p className="mt-3 font-serif text-sm leading-relaxed opacity-80">{p.desc}</p>
                  {p.soldOut ? (
                    <SoldOutRow price={p.price} size={p.size} />
                  ) : (
                    <BuyNowRow price={p.price} size={p.size} productName={p.name} slug={p.slug} image={imgFor(p)} testIdPrefix={`rub-${i}`} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE BUNDLE */}
      <section className="border-b border-ink bg-bone py-20 md:py-28" data-testid="bundles-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="Family Bundle" title="Buy Three · Save Six" />
          {bundlesShown.map((b) => (
            <div
              key={b.name}
              data-testid="bundle-family-3pack"
              className="mt-12 grid grid-cols-1 gap-0 border border-ink shadow-hard md:grid-cols-12"
            >
              <div className="grain-block relative aspect-[4/3] overflow-hidden bg-ink md:col-span-6">
                <img src={imgFor(b)} alt={b.name} className="h-full w-full object-cover" />
              </div>
              <div className="flex flex-col justify-center p-8 md:col-span-6 md:p-14">
                <span className="mb-4 inline-block w-max border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em]">{b.tag}</span>
                <h3 className="font-rye text-3xl uppercase leading-none md:text-5xl">{b.name}</h3>
                <p className="mt-3 font-serif text-base italic opacity-80">{b.tagline}</p>
                <p className="mt-6 max-w-md font-serif text-base leading-relaxed opacity-85">{b.desc}</p>
                <div className="mt-8">
                  <BuyNowRow price={b.price} size={b.size} productName={b.name} slug={b.slug} image={imgFor(b)} testIdPrefix="bundle-family-3pack" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SAUCES */}
      <section className="border-b border-ink bg-white py-20 md:py-28" data-testid="sauces-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="The Sauces" title="Bottles & Splashes" subtitle="Signature sauces from Tillman's Barbecue and our own competition pit crew." />
          <div className="mt-12 grid grid-cols-1 gap-0 border border-ink sm:grid-cols-2 lg:grid-cols-4">
            {saucesShown.map((s, i) => (
              <div
                key={s.name}
                data-testid={`sauce-${i}`}
                className={`group flex flex-col border-ink ${((i + 1) % 2 !== 0) ? "sm:border-r" : ""} ${((i + 1) % 4 !== 0) ? "lg:border-r" : ""} ${i < saucesShown.length - (saucesShown.length % 4 || 4) ? "border-b" : ""}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-white">
                  {s.img ? (
                    <img
                      src={imgFor(s)}
                      alt={s.name}
                      className="h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-ink text-bone/40">
                      <span className="font-alfa text-6xl uppercase">{s.name.split(" ").pop().slice(0, 2)}</span>
                    </div>
                  )}
                  {s.tag && (
                    <span className="absolute left-4 top-4 border border-ink bg-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-bone">
                      {s.tag}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center justify-between border-b border-current pb-3 font-mono text-[10px] uppercase tracking-[0.24em]">
                    <span>Sauce · 0{i + 1}</span>
                    <span>{s.size}</span>
                  </div>
                  <h3 className="mt-4 font-alfa text-xl uppercase leading-tight">{s.name}</h3>
                  <p className="mt-2 font-serif text-sm leading-relaxed opacity-80">{s.desc}</p>
                  <BuyNowRow price={s.price} size={s.size} productName={s.name} slug={s.slug} image={imgFor(s)} testIdPrefix={`sauce-${i}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MERCH */}
      <section className="border-b border-ink bg-bone py-20 md:py-28" data-testid="merch-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="Wear the Brand" title="Merchandise" />
          <div className="mt-12 grid grid-cols-1 gap-0 border border-ink sm:grid-cols-2 lg:grid-cols-3">
            {merchShown.map((m, i) => (
              <MerchCard
                key={m.name}
                m={m}
                idx={i}
                isLast={i === merchShown.length - 1}
                total={merchShown.length}
                imgBack={imgFor(m)}
                imgFront={m.imgFront ? ASSETS[m.imgFront] : null}
              />
            ))}
          </div>

          <p className="mt-10 max-w-2xl font-serif text-sm text-ink/70">
            Checkout opens in a new tab via PayPal. Free U.S. shipping on orders over $50. Wholesale &amp; retailer inquiries: <a href={`mailto:${BUSINESS.email}`} className="link-underline">{BUSINESS.email}</a>.
          </p>
        </div>
      </section>
    </div>
  );
}
